import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  EmailTemplateData,
  Options,
  StartupMatchRefreshStatusInput,
  V2AdminListBlogPostsData,
} from '@workspace/api-backend'
import {
  getDocumentDownloadUrlMutation,
  v2AdminAddInvestorCreditsMutation,
  v2AdminAddStartupCreditsMutation,
  v2AdminAgentOverviewOptions,
  v2AdminAgentSessionCallsOptions,
  v2AdminChangeUserEmailMutation,
  v2AdminCreateBlogPostMutation,
  v2AdminCreateHelpArticleMutation,
  v2AdminCreateHelpCategoryMutation,
  v2AdminDecideInvestorCheckMutation,
  v2AdminDeleteBlogPostMutation,
  v2AdminDeleteEvaluationMutation,
  v2AdminDeleteHelpArticleMutation,
  v2AdminDeleteHelpCategoryMutation,
  v2AdminDeletePitchSessionMutation,
  v2AdminDeletePrescreenMutation,
  v2AdminDeleteStartupMutation,
  v2AdminDeleteUserMutation,
  v2AdminGetAutomatedEmailByIdOptions,
  v2AdminGetAutomatedEmailByIdQueryKey,
  v2AdminGetBlogPostOptions,
  v2AdminGetBlogPostQueryKey,
  v2AdminGetDashboardOptions,
  v2AdminGetDataRoomAnalyticsOptions,
  v2AdminGetDataRoomByIdOptions,
  v2AdminGetEmailTemplateOptions,
  v2AdminGetEmailTemplateQueryKey,
  v2AdminGetEvaluationDetailsOptions,
  v2AdminGetEvaluationDetailsQueryKey,
  v2AdminGetEvaluationViewOptions,
  v2AdminGetEvaluationViewQueryKey,
  v2AdminGetHelpArticleOptions,
  v2AdminGetHelpArticleQueryKey,
  v2AdminGetInvestorByIdOptions,
  v2AdminGetInvestorByIdQueryKey,
  v2AdminGetInvestorIndexStatusOptions,
  v2AdminGetInvestorIndexStatusQueryKey,
  v2AdminGetInvestorMembershipsOptions,
  v2AdminGetInvestorPersonaOptions,
  v2AdminGetInvestorPersonaQueryKey,
  v2AdminGetInvestorThesisOptions,
  v2AdminGetSignupQuestionOptions,
  v2AdminGetSignupQuestionQueryKey,
  v2AdminGetStartupBillingOptions,
  v2AdminGetStartupBillingQueryKey,
  v2AdminGetStartupByIdOptions,
  v2AdminGetStartupByIdQueryKey,
  v2AdminGetStartupDeletionPreviewOptions,
  v2AdminGetStartupDocumentsOptions,
  v2AdminGetStartupEmailUpdatesOptions,
  v2AdminGetStartupMatchesRefreshStatusOptions,
  v2AdminGetStartupMatchesRefreshStatusQueryKey,
  v2AdminGetStartupMembershipsOptions,
  v2AdminGetStartupOutreachContactsOptions,
  v2AdminGetStartupTeamMembersOptions,
  v2AdminGetUserByIdOptions,
  v2AdminGetUserByIdQueryKey,
  v2AdminGetUserDeletionPreviewOptions,
  v2AdminGetUserMembershipsOptions,
  v2AdminGrantStartupEntitlementMutation,
  v2AdminGrantStartupUsageMutation,
  v2AdminListActivityResearchOptions,
  v2AdminListActivityResearchQueryKey,
  v2AdminListAiInsightReportsOptions,
  v2AdminListAiInsightReportsQueryKey,
  v2AdminListAutomatedEmailsOptions,
  v2AdminListAutomatedEmailsQueryKey,
  v2AdminListBlogPostsOptions,
  v2AdminListBlogPostsQueryKey,
  v2AdminListDataRoomsOptions,
  v2AdminListEmailTemplatesOptions,
  v2AdminListEmailTemplatesQueryKey,
  v2AdminListEvaluationsOptions,
  v2AdminListEvaluationsQueryKey,
  v2AdminListFeedbackOptions,
  v2AdminListHelpCategoriesOptions,
  v2AdminListHelpCategoriesQueryKey,
  v2AdminListInvestorChecksOptions,
  v2AdminListInvestorChecksQueryKey,
  v2AdminListInvestorCreditsOptions,
  v2AdminListInvestorCreditsQueryKey,
  v2AdminListInvestorsOptions,
  v2AdminListInvestorsQueryKey,
  v2AdminListMatcherJobsOptions,
  v2AdminListObserverAlertsOptions,
  v2AdminListObserversOptions,
  v2AdminListStartupCreditsOptions,
  v2AdminListStartupCreditsQueryKey,
  v2AdminListStartupEntitlementsOptions,
  v2AdminListStartupEntitlementsQueryKey,
  v2AdminListStartupFactSourcesOptions,
  v2AdminListStartupFactSourcesQueryKey,
  v2AdminListStartupFactsOptions,
  v2AdminListStartupFactsQueryKey,
  v2AdminListStartupMatchesOptions,
  v2AdminListStartupMatchesQueryKey,
  v2AdminListStartupReactionsOptions,
  v2AdminListStartupsOptions,
  v2AdminListStartupsQueryKey,
  v2AdminListStartupTextFeedbacksOptions,
  v2AdminListUsersOptions,
  v2AdminListUsersQueryKey,
  v2AdminListUserTermsAcceptancesOptions,
  v2AdminPublishInvestorIndexMutation,
  v2AdminRefreshStartupMatchesMutation,
  v2AdminRegenerateAiInsightReportMutation,
  v2AdminRegenerateEvaluationMutation,
  v2AdminReorderHelpArticlesMutation,
  v2AdminReorderHelpCategoriesMutation,
  v2AdminRerunPrescreenMutation,
  v2AdminRescanStartupFactSourceMutation,
  v2AdminResetStartupGrantsMutation,
  v2AdminRevokeStartupEntitlementMutation,
  v2AdminSendAutomatedEmailMutation,
  v2AdminSendTestEmailTemplateMutation,
  v2AdminSetStartupInternalMutation,
  v2AdminSetStartupProfileChatLimitMutation,
  v2AdminTriggerActivityResearchMutation,
  v2AdminTriggerPersonaResearchMutation,
  v2AdminTriggerPrescreenMutation,
  v2AdminUpdateBlogPostMutation,
  v2AdminUpdateEmailTemplateMutation,
  v2AdminUpdateHelpArticleMutation,
  v2AdminUpdateHelpCategoryMutation,
  v2AdminUpdateInvestorMutation,
  v2AdminUpdateSignupQuestionMutation,
  v2GetJudgeProfileOptions,
  v2GetPitchVideoUrlMutation,
  v2GetPublicProfileFullOptions,
} from '@workspace/api-backend/react-query'
import { MATCHER_REFRESH_POLL_MS } from '../../../lib/matches'

/** The discriminated email-type union shared by all email-template endpoints. */
export type EmailTemplateType = EmailTemplateData['emailType']

// ─── Types for admin API responses ──────────────────────────────────────────

export type AdminMembershipItem = {
  userId: string | null
  name: string | null
  email: string | null
  role: string
  position: string | null
  /** The MCP text set an investor member is pinned to; startup members have none. */
  mcpTextSet?: string | null
}

export type AdminUserMemberships = {
  startups: Array<{
    id: string
    name: string
    role: string
    position: string | null
    email: string | null
    status: string
  }>
  investors: Array<{
    id: string
    name: string
    role: string
    position: string | null
    slug: string | null
  }>
}

export type AdminUnifiedEvaluationItem = {
  id: string
  type: string
  status: string
  score: number | null
  startupName: string | null
  startupId: string | null
  investorName: string | null
  investorId: string | null
  personaName: string | null
  source: string
  createdAt: string
}

export type AdminEvaluationPitchSession = {
  id: string
  status: string
  transcript: string | null
  questions: string | null
  teamResearch: string | null
  marketResearch: string | null
  videoKey: string | null
  videoCoverImageUrl: string | null
  pitchDeckId: string | null
  startupId: string | null
  startup: { name: string } | null
  investorId: string | null
  investor: { name: string } | null
  isPublished: boolean
  createdAt: string
  finishedAt: string | null
}

export type AdminEvaluationPrescreen = {
  id: string
  status: string
  startupId: string | null
  startup: { name: string } | null
  persona: { name: string; investor: { name: string } | null } | null
  pitchDeckId: string | null
}

export type AdminMetricEvaluation = {
  id: string
  metric: string
  score: number | null
}

export type AdminPersonaEvaluation = {
  id: string
  persona: string
  decision: string | null
  score: number | null
  decisionThesis: string | null
  metricEvaluations: AdminMetricEvaluation[]
}

export type AdminEvaluationDetails = {
  id: string
  type: string
  status: string
  score: number | null
  winnerPersona: string | null
  verdict: string | null
  createdAt: string
  pitchDeckFullText: string | null
  investorId: string | null
  pitchSession: AdminEvaluationPitchSession | null
  prescreenRequest: AdminEvaluationPrescreen | null
  persona: { name: string } | null
  collectiveMetricEvaluations: AdminMetricEvaluation[]
  personaEvaluations: AdminPersonaEvaluation[]
}

export type AdminPersonaResearchRequest = {
  id: string
  status: string
  version: string
  createdAt: string
  error: string | null
}

export type AdminInvestorPersonaItem = {
  id: string
  name: string
  brief: string
  description: string
  version: number
  source: string | null
  createdAt: string
  metricPrompts: Array<{
    id: string
    metric: string
    stage: string
    prompt: string
    weight: number
  }>
}

export type AdminInvestorPersonaData = {
  personas: AdminInvestorPersonaItem[]
  researchRequests: AdminPersonaResearchRequest[]
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export function useAdminDashboardQuery(days: number | 'all') {
  return useQuery(v2AdminGetDashboardOptions({ query: { days } }))
}

// ─── Users ───────────────────────────────────────────────────────────────────

export function useAdminUsersQuery(query: { page?: number; limit?: number; search?: string }) {
  return useQuery(v2AdminListUsersOptions({ query }))
}

export function useAdminUserByIdQuery(id: string) {
  return useQuery(v2AdminGetUserByIdOptions({ path: { id } }))
}

/**
 * Gated on `enabled` because the preview now performs a live HeyReach lookup to
 * find teardown blockers. Left ungated it would hit HeyReach on every visit to
 * the admin user page, not when the delete dialog is opened. Mirrors
 * `useAdminStartupDeletionPreviewQuery`.
 */
export function useAdminUserDeletionPreviewQuery(id: string, enabled = true) {
  return useQuery({
    ...v2AdminGetUserDeletionPreviewOptions({ path: { id } }),
    enabled,
  })
}

export function useAdminStartupDeletionPreviewQuery(id: string, enabled = true) {
  return useQuery({
    ...v2AdminGetStartupDeletionPreviewOptions({ path: { id } }),
    enabled,
  })
}

export function useAdminUserMembershipsQuery(userId: string) {
  return useQuery(v2AdminGetUserMembershipsOptions({ path: { id: userId } }))
}

export function useAdminUserTermsAcceptancesQuery(userId: string) {
  return useQuery(v2AdminListUserTermsAcceptancesOptions({ path: { id: userId } }))
}

// ─── Startups ────────────────────────────────────────────────────────────────

export function useAdminStartupsQuery(query: {
  page?: number
  limit?: number
  search?: string
  hasPrescreen?: boolean
}) {
  return useQuery(v2AdminListStartupsOptions({ query }))
}

export function useAdminStartupByIdQuery(id: string) {
  return useQuery(v2AdminGetStartupByIdOptions({ path: { id } }))
}

export function useAdminStartupMembershipsQuery(id: string) {
  return useQuery(v2AdminGetStartupMembershipsOptions({ path: { id } }))
}

export function useAdminStartupTeamMembersQuery(id: string) {
  return useQuery(v2AdminGetStartupTeamMembersOptions({ path: { id } }))
}

export function useAdminStartupCreditsQuery(id: string) {
  return useQuery(v2AdminListStartupCreditsOptions({ path: { id } }))
}

export function useAdminStartupMatchesQuery(id: string, page: number, pageSize: number) {
  return useQuery(
    v2AdminListStartupMatchesOptions({
      path: { id },
      query: { limit: pageSize, offset: page * pageSize },
    })
  )
}

export function useAdminStartupOutreachContactsQuery(id: string) {
  // generateMissing must stay false on the read path — otherwise loading the
  // admin page would trigger LLM gen for every null row (30+ contacts × ~10s).
  // The regenerate button calls a dedicated endpoint that synchronously
  // regenerates a single contact before returning, so the subsequent refetch
  // reads an already-populated row.
  return useQuery(
    v2AdminGetStartupOutreachContactsOptions({
      path: { id },
      query: { limit: 200 },
    })
  )
}

export function useAdminStartupMatchesRefreshStatusQuery(id: string) {
  return useQuery({
    ...v2AdminGetStartupMatchesRefreshStatusOptions({ path: { id } }),
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status
      return status === 'PENDING' || status === 'ENQUEUED' || status === 'PROCESSING'
        ? MATCHER_REFRESH_POLL_MS
        : false
    },
  })
}

export function useAdminRefreshStartupMatchesMutation(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminRefreshStartupMatchesMutation(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: v2AdminListStartupMatchesQueryKey({ path: { id }, query: {} }),
        }),
        queryClient.invalidateQueries({
          queryKey: v2AdminGetStartupMatchesRefreshStatusQueryKey({ path: { id } }),
        }),
      ])
    },
  })
}

// ─── Matcher Jobs ───────────────────────────────────────────────────────────

export function useAdminMatcherJobsQuery(
  page: number,
  status: StartupMatchRefreshStatusInput | undefined
) {
  return useQuery(v2AdminListMatcherJobsOptions({ query: { page, status } }))
}

// ─── Startup Billing (pay-as-you-go) ─────────────────────────────────────────

export function useAdminStartupBillingQuery(id: string) {
  return useQuery(v2AdminGetStartupBillingOptions({ path: { id } }))
}

export function useAdminGrantStartupUsageMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminGrantStartupUsageMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminGetStartupBillingQueryKey({ path: { id: startupId } }),
      })
      queryClient.invalidateQueries({
        queryKey: v2AdminGetStartupByIdQueryKey({ path: { id: startupId } }),
      })
    },
  })
}

export function useAdminSetStartupProfileChatLimitMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminSetStartupProfileChatLimitMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminGetStartupByIdQueryKey({ path: { id: startupId } }),
      })
    },
  })
}

export function useAdminSetStartupInternalMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminSetStartupInternalMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminGetStartupByIdQueryKey({ path: { id: startupId } }),
      })
    },
  })
}

export function useAdminResetStartupGrantsMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminResetStartupGrantsMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminGetStartupBillingQueryKey({ path: { id: startupId } }),
      })
    },
  })
}

// ─── Investor account checks ────────────────────────────────────────────────

export function useAdminInvestorChecksQuery(query: {
  status: 'PENDING' | 'PASSED' | 'FAILED'
  page: number
  limit: number
}) {
  return useQuery(v2AdminListInvestorChecksOptions({ query }))
}

export function useAdminDecideInvestorCheckMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminDecideInvestorCheckMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListInvestorChecksQueryKey() })
    },
  })
}

// ─── Investor observers ─────────────────────────────────────────────────────

export function useAdminStartupEmailUpdatesQuery(startupId: string) {
  return useQuery({
    ...v2AdminGetStartupEmailUpdatesOptions({ path: { id: startupId } }),
    // Mail arrives while the page is open.
    refetchInterval: 15_000,
  })
}

export function useAdminInvestorThesisQuery(investorId: string) {
  return useQuery(v2AdminGetInvestorThesisOptions({ path: { id: investorId } }))
}

export function useAdminObserverAlertsQuery(observerId: string, enabled: boolean) {
  return useQuery({ ...v2AdminListObserverAlertsOptions({ path: { observerId } }), enabled })
}

export function useAdminObserversQuery(query: {
  status?: 'ACTIVE' | 'WAITING' | 'FIRED' | 'ENDED_BY_INVESTOR' | 'REMOVED_BY_FOUNDER'
  investorId?: string
  classification?: 'pending' | 'failed'
  page: number
  limit: number
}) {
  return useQuery(v2AdminListObserversOptions({ query }))
}

// ─── Investor AI agent calls ────────────────────────────────────────────────

export function useAdminAgentOverviewQuery(query: {
  tool?: 'FIND' | 'READ' | 'CHECK' | 'NOTE' | 'JOURNAL' | 'THESIS'
  investorId?: string
  from?: Date
  page: number
  limit: number
}) {
  return useQuery({
    ...v2AdminAgentOverviewOptions({ query }),
    // Job health is the point of the page: keep it fresh while it is open.
    refetchInterval: 30_000,
  })
}

export function useAdminAgentSessionCallsQuery(sessionId: string, enabled: boolean) {
  return useQuery({ ...v2AdminAgentSessionCallsOptions({ path: { sessionId } }), enabled })
}

// ─── Startup facts ──────────────────────────────────────────────────────────

export function useAdminStartupFactsQuery(
  startupId: string,
  query: {
    current: boolean
    sourceKind?: 'DECK' | 'DOCUMENT' | 'EMAIL_UPDATE' | 'FOUNDER_SPEECH'
    dimension?: string
    kind?: string
    q?: string
    page: number
    limit: number
  }
) {
  return useQuery(
    v2AdminListStartupFactsOptions({
      path: { id: startupId },
      // The generated query type narrows `dimension` to the ten known names.
      query: query as Parameters<typeof v2AdminListStartupFactsOptions>[0]['query'],
    })
  )
}

export function useAdminStartupFactSourcesQuery(startupId: string) {
  return useQuery(v2AdminListStartupFactSourcesOptions({ path: { id: startupId } }))
}

export function useAdminRescanStartupFactSourceMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminRescanStartupFactSourceMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminListStartupFactSourcesQueryKey({ path: { id: startupId } }),
      })
      queryClient.invalidateQueries({
        queryKey: v2AdminListStartupFactsQueryKey({ path: { id: startupId } }),
      })
    },
  })
}

// ─── Startup Entitlements ───────────────────────────────────────────────────

export function useAdminStartupEntitlementsQuery(startupId: string) {
  return useQuery(v2AdminListStartupEntitlementsOptions({ path: { startupId } }))
}

export function useAdminGrantStartupEntitlementMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminGrantStartupEntitlementMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminListStartupEntitlementsQueryKey({ path: { startupId } }),
      })
    },
  })
}

export function useAdminRevokeStartupEntitlementMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminRevokeStartupEntitlementMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminListStartupEntitlementsQueryKey({ path: { startupId } }),
      })
    },
  })
}

// ─── Investors ───────────────────────────────────────────────────────────────

export function useAdminInvestorsQuery(query: {
  page?: number
  limit?: number
  search?: string
  hasPersona?: boolean
}) {
  return useQuery(v2AdminListInvestorsOptions({ query }))
}

export function useAdminInvestorByIdQuery(id: string) {
  return useQuery(v2AdminGetInvestorByIdOptions({ path: { id } }))
}

export function useAdminInvestorMembershipsQuery(id: string) {
  return useQuery(v2AdminGetInvestorMembershipsOptions({ path: { id } }))
}

export function useAdminInvestorPersonaQuery(id: string) {
  return useQuery(v2AdminGetInvestorPersonaOptions({ path: { id } }))
}

export function useAdminInvestorCreditsQuery(id: string) {
  return useQuery(v2AdminListInvestorCreditsOptions({ path: { id } }))
}

// ─── Evaluations ─────────────────────────────────────────────────────────────

export function useAdminEvaluationsQuery(query: {
  page?: number
  limit?: number
  search?: string
  status?: string
}) {
  return useQuery(v2AdminListEvaluationsOptions({ query }))
}

export function useAdminEvaluationViewQuery(id: string) {
  return useQuery(v2AdminGetEvaluationViewOptions({ path: { id } }))
}

export function useAdminEvaluationDetailsQuery(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    ...v2AdminGetEvaluationDetailsOptions({ path: { id } }),
    ...options,
  })
}

export function useAdminJudgeProfileQuery(
  startupId: string,
  evaluationId: string,
  judgeKey: string
) {
  return useQuery({
    ...v2GetJudgeProfileOptions({ path: { startupId, evaluationId, judgeKey } }),
    enabled: !!startupId && !!evaluationId && !!judgeKey,
  })
}

// ─── Startup Documents ───────────────────────────────────────────────────────

export function useAdminStartupDocumentsQuery(startupId: string) {
  return useQuery(v2AdminGetStartupDocumentsOptions({ path: { startupId } }))
}

// ─── Feedback ────────────────────────────────────────────────────────────────

export function useAdminFeedbackQuery(query: { page?: number; limit?: number; search?: string }) {
  return useQuery(v2AdminListFeedbackOptions({ query }))
}

// ─── Data Rooms ──────────────────────────────────────────────────────────────

export function useAdminDataRoomsQuery(query: { page?: number; limit?: number; search?: string }) {
  return useQuery(v2AdminListDataRoomsOptions({ query }))
}

export function useAdminDataRoomByIdQuery(id: string) {
  return useQuery(v2AdminGetDataRoomByIdOptions({ path: { id } }))
}

export function useAdminDataRoomAnalyticsQuery(id: string) {
  return useQuery(v2AdminGetDataRoomAnalyticsOptions({ path: { id } }))
}

// ─── AI Insight Reports ──────────────────────────────────────────────────────

export function useAdminAiInsightReportsQuery() {
  return useQuery(v2AdminListAiInsightReportsOptions())
}

export function useAdminRegenerateAiInsightReportMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminRegenerateAiInsightReportMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListAiInsightReportsQueryKey() })
    },
  })
}

// ─── Startup Insights (text feedbacks + reactions) ──────────────────────────

export function useAdminStartupTextFeedbacksQuery(startupId: string) {
  return useQuery(v2AdminListStartupTextFeedbacksOptions({ path: { id: startupId } }))
}

export function useAdminStartupReactionsQuery(startupId: string) {
  return useQuery(v2AdminListStartupReactionsOptions({ path: { id: startupId } }))
}

// ─── Automated Emails ────────────────────────────────────────────────────────

type AutomatedEmailQuery = NonNullable<
  Parameters<typeof v2AdminListAutomatedEmailsOptions>[0]
>['query']

export function useAdminAutomatedEmailsQuery(query: {
  page?: number
  limit?: number
  search?: string
  emailType?: string
  status?: string
}) {
  return useQuery(v2AdminListAutomatedEmailsOptions({ query: query as AutomatedEmailQuery }))
}

export function useAdminAutomatedEmailByIdQuery(id: string) {
  return useQuery(v2AdminGetAutomatedEmailByIdOptions({ path: { id } }))
}

export function useAdminSendAutomatedEmailMutation(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminSendAutomatedEmailMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListAutomatedEmailsQueryKey() })
      queryClient.invalidateQueries({
        queryKey: v2AdminGetAutomatedEmailByIdQueryKey({ path: { id } }),
      })
    },
  })
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useAdminDeleteUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminDeleteUserMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListUsersQueryKey() })
    },
  })
}

export function useAdminDeleteStartupMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminDeleteStartupMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListStartupsQueryKey() })
    },
  })
}

export function useAdminChangeUserEmailMutation(userId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminChangeUserEmailMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminGetUserByIdQueryKey({ path: { id: userId } }),
      })
      queryClient.invalidateQueries({ queryKey: v2AdminListUsersQueryKey() })
    },
  })
}

export function useAdminAddStartupCreditsMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminAddStartupCreditsMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminListStartupCreditsQueryKey({ path: { id: startupId } }),
      })
      queryClient.invalidateQueries({
        queryKey: v2AdminGetStartupByIdQueryKey({ path: { id: startupId } }),
      })
    },
  })
}

export function useAdminAddInvestorCreditsMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminAddInvestorCreditsMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminListInvestorCreditsQueryKey({ path: { id: investorId } }),
      })
      queryClient.invalidateQueries({
        queryKey: v2AdminGetInvestorByIdQueryKey({ path: { id: investorId } }),
      })
    },
  })
}

export function useAdminUpdateInvestorMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminUpdateInvestorMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminGetInvestorByIdQueryKey({ path: { id: investorId } }),
      })
      queryClient.invalidateQueries({ queryKey: v2AdminListInvestorsQueryKey() })
    },
  })
}

export function useAdminRegenerateEvaluationMutation(evaluationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminRegenerateEvaluationMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminGetEvaluationViewQueryKey({ path: { id: evaluationId } }),
      })
      queryClient.invalidateQueries({
        queryKey: v2AdminGetEvaluationDetailsQueryKey({ path: { id: evaluationId } }),
      })
    },
  })
}

export function useAdminInvestorActivityResearchQuery(investorId: string) {
  return useQuery({
    ...v2AdminListActivityResearchOptions({ query: { investorId, page: 1 } }),
    refetchInterval: (query) => {
      const status = query.state.data?.data?.items?.[0]?.status
      return status === 'PENDING' || status === 'ENQUEUED' || status === 'ACTIVE' ? 5000 : false
    },
  })
}

export function useAdminTriggerActivityResearchMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminTriggerActivityResearchMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminListActivityResearchQueryKey({ query: { investorId, page: 1 } }),
      })
    },
  })
}

export function useAdminTriggerPersonaResearchMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminTriggerPersonaResearchMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2AdminGetInvestorPersonaQueryKey({ path: { id: investorId } }),
      })
    },
  })
}

export function useAdminRerunPrescreenMutation() {
  return useMutation(v2AdminRerunPrescreenMutation())
}

export function useAdminTriggerPrescreenMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminTriggerPrescreenMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListStartupsQueryKey() })
    },
  })
}

export function useAdminDeletePrescreenMutation() {
  return useMutation(v2AdminDeletePrescreenMutation())
}

export function useAdminDeletePitchSessionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminDeletePitchSessionMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListEvaluationsQueryKey() })
    },
  })
}

export function useAdminDeleteEvaluationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminDeleteEvaluationMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListEvaluationsQueryKey() })
    },
  })
}

// ─── Utility hooks (re-exported for admin pages) ────────────────────────────

export function useAdminDocumentDownloadUrlMutation() {
  return useMutation(getDocumentDownloadUrlMutation())
}

export function useAdminPitchVideoUrlMutation() {
  return useMutation(v2GetPitchVideoUrlMutation())
}

export function useAdminPublicProfileFullQuery(token: string, enabled = true) {
  return useQuery({
    ...v2GetPublicProfileFullOptions({ path: { token } }),
    enabled,
  })
}

// ─── Blog Post hooks ────────────────────────────────────────────────────────

export function useAdminBlogPostsQuery(query: {
  page?: number
  limit?: number
  search?: string
  status?: string
}) {
  return useQuery(
    v2AdminListBlogPostsOptions({
      query: query as NonNullable<Options<V2AdminListBlogPostsData>['query']>,
    })
  )
}

export function useAdminBlogPostByIdQuery(id: string) {
  return useQuery({
    ...v2AdminGetBlogPostOptions({ path: { id } }),
    enabled: !!id,
  })
}

export function useAdminCreateBlogPostMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminCreateBlogPostMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListBlogPostsQueryKey() })
    },
  })
}

export function useAdminUpdateBlogPostMutation(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminUpdateBlogPostMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListBlogPostsQueryKey() })
      queryClient.invalidateQueries({ queryKey: v2AdminGetBlogPostQueryKey({ path: { id } }) })
    },
  })
}

export function useAdminDeleteBlogPostMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminDeleteBlogPostMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListBlogPostsQueryKey() })
    },
  })
}

// ─── Email Template hooks ─────────────────────────────────────────────────────

export function useAdminEmailTemplatesQuery() {
  return useQuery(v2AdminListEmailTemplatesOptions())
}

export function useAdminEmailTemplateByTypeQuery(type: EmailTemplateType) {
  return useQuery({
    ...v2AdminGetEmailTemplateOptions({ path: { type } }),
    enabled: !!type,
  })
}

export function useAdminUpdateEmailTemplateMutation(type: EmailTemplateType) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminUpdateEmailTemplateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListEmailTemplatesQueryKey() })
      queryClient.invalidateQueries({
        queryKey: v2AdminGetEmailTemplateQueryKey({ path: { type } }),
      })
    },
  })
}

export function useAdminSendTestEmailTemplateMutation() {
  return useMutation(v2AdminSendTestEmailTemplateMutation())
}

// ─── Sign-up question hooks ───────────────────────────────────────────────────

export function useAdminSignupQuestionQuery() {
  return useQuery(v2AdminGetSignupQuestionOptions())
}

export function useAdminUpdateSignupQuestionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminUpdateSignupQuestionMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminGetSignupQuestionQueryKey() })
    },
  })
}

// ─── Help Center hooks ──────────────────────────────────────────────────────

export function useAdminHelpCategoriesQuery() {
  return useQuery(v2AdminListHelpCategoriesOptions())
}

export function useAdminCreateHelpCategoryMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminCreateHelpCategoryMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListHelpCategoriesQueryKey() })
    },
  })
}

export function useAdminUpdateHelpCategoryMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminUpdateHelpCategoryMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListHelpCategoriesQueryKey() })
    },
  })
}

export function useAdminDeleteHelpCategoryMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminDeleteHelpCategoryMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListHelpCategoriesQueryKey() })
    },
  })
}

export function useAdminReorderHelpCategoriesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminReorderHelpCategoriesMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListHelpCategoriesQueryKey() })
    },
  })
}

export function useAdminHelpArticleByIdQuery(id: string) {
  return useQuery({
    ...v2AdminGetHelpArticleOptions({ path: { id } }),
    enabled: !!id,
  })
}

export function useAdminCreateHelpArticleMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminCreateHelpArticleMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListHelpCategoriesQueryKey() })
    },
  })
}

export function useAdminUpdateHelpArticleMutation(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminUpdateHelpArticleMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListHelpCategoriesQueryKey() })
      queryClient.invalidateQueries({ queryKey: v2AdminGetHelpArticleQueryKey({ path: { id } }) })
    },
  })
}

export function useAdminDeleteHelpArticleMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminDeleteHelpArticleMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListHelpCategoriesQueryKey() })
    },
  })
}

export function useAdminReorderHelpArticlesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminReorderHelpArticlesMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListHelpCategoriesQueryKey() })
    },
  })
}

// ─── Investor Index hooks ───────────────────────────────────────────────────

export function useAdminInvestorIndexStatusQuery() {
  return useQuery(v2AdminGetInvestorIndexStatusOptions())
}

export function useAdminPublishInvestorIndexMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminPublishInvestorIndexMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminGetInvestorIndexStatusQueryKey() })
    },
  })
}
